import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft, CreditCard, Wallet, Crown, ExternalLink, Loader2,
  User, Mail, Calendar, FileText, MessageCircle, Coins, Shield,
  TrendingUp, ChevronRight, Clock, CheckCircle, AlertCircle, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Footer from './components/Footer';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MyAccountPage = ({ onBack, onNavigatePricing }) => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    if (token) {
      Promise.all([loadSummary(), loadHistory()]).finally(() => setLoading(false));
    }
  }, [token]);

  const loadSummary = async () => {
    try {
      const res = await axios.get(`${API}/account-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(res.data);
    } catch (err) {
      console.error('Error loading summary:', err);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/payment-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data);
    } catch (err) {
      console.error('Error loading history:', err);
    }
  };

  const openCustomerPortal = async () => {
    window.open('https://billing.stripe.com/p/login/28E3cv2HbeWhgZh6pW04800', '_blank');
  };

  const formatCurrency = (v) => `R$ ${(v || 0).toFixed(2).replace('.', ',')}`;

  const planColors = {
    free: '#888', basico: '#3b82f6', premium: '#00ffff', empresarial: '#8b5cf6'
  };

  const getStatusStyle = (status) => {
    if (status === 'paid' || status === 'completed') return { bg: 'rgba(34,197,94,0.1)', color: '#22c55e', label: 'Pago', icon: <CheckCircle className="w-3.5 h-3.5" /> };
    if (status === 'pending' || status === 'initiated') return { bg: 'rgba(245,158,11,0.1)', color: '#f59e0b', label: 'Pendente', icon: <Clock className="w-3.5 h-3.5" /> };
    return { bg: 'rgba(239,68,68,0.1)', color: '#ef4444', label: 'Falhou', icon: <XCircle className="w-3.5 h-3.5" /> };
  };

  const typeLabels = { subscription: 'Assinatura', credits: 'Créditos', consultancy: 'Consultoria' };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#00ffff' }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '900px', margin: '0 auto' }} data-testid="my-account-page">
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <Button variant="ghost" onClick={onBack} style={{ marginBottom: '1rem' }} data-testid="account-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <h1 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.25rem' }}>Minha Conta</h1>
        <p style={{ color: '#aaa', fontSize: '0.85rem' }}>Gerencie sua assinatura, pagamentos e dados</p>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
          {[
            { id: 'overview', label: 'Visão Geral', icon: <User className="w-4 h-4" /> },
            { id: 'subscription', label: 'Assinatura', icon: <Crown className="w-4 h-4" /> },
            { id: 'payments', label: 'Pagamentos', icon: <CreditCard className="w-4 h-4" /> },
            { id: 'history', label: 'Histórico', icon: <FileText className="w-4 h-4" /> }
          ].map(tab => (
            <Button
              key={tab.id}
              variant={activeSection === tab.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveSection(tab.id)}
              data-testid={`account-tab-${tab.id}`}
              style={activeSection === tab.id ? { background: '#00ffff', color: '#000' } : {}}
            >
              {tab.icon}
              <span style={{ marginLeft: '0.5rem' }}>{tab.label}</span>
            </Button>
          ))}
        </div>
      </div>

      {/* Overview */}
      {activeSection === 'overview' && summary && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {/* User Card */}
          <Card style={{ background: '#1a1a1a', border: '1px solid #333' }}>
            <CardContent style={{ padding: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: planColors[summary.plan] || '#888', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', fontWeight: '700', color: '#fff' }}>
                  {(summary.name || summary.email || '?')[0].toUpperCase()}
                </div>
                <div>
                  <h2 style={{ fontWeight: '600', fontSize: '1.1rem' }}>{summary.name || summary.email}</h2>
                  <p style={{ color: '#888', fontSize: '0.85rem' }}>{summary.email}</p>
                </div>
                <Badge style={{ marginLeft: 'auto', background: `${planColors[summary.plan]}20`, color: planColors[summary.plan], fontWeight: '600' }}>
                  {summary.plan_name}
                </Badge>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem' }}>
                {[
                  { icon: <Coins className="w-4 h-4" />, label: 'Créditos', value: summary.credits_available >= 999999 ? 'Ilimitados' : summary.credits_available, color: '#f59e0b' },
                  { icon: <TrendingUp className="w-4 h-4" />, label: 'Usados este mês', value: summary.credits_used_this_month, color: '#00ffff' },
                  { icon: <MessageCircle className="w-4 h-4" />, label: 'Conversas', value: summary.conversations_count, color: '#8b5cf6' },
                  { icon: <FileText className="w-4 h-4" />, label: 'Documentos', value: summary.files_count, color: '#22c55e' }
                ].map((stat, i) => (
                  <div key={i} style={{ background: '#0f0f0f', borderRadius: '8px', padding: '0.75rem', textAlign: 'center' }}>
                    <div style={{ color: stat.color, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem', marginBottom: '0.25rem' }}>
                      {stat.icon}
                      <span style={{ fontSize: '0.7rem', color: '#888' }}>{stat.label}</span>
                    </div>
                    <p style={{ fontSize: '1.25rem', fontWeight: '700', color: stat.color }}>{stat.value}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <Card
              style={{ background: '#1a1a1a', border: '1px solid #333', cursor: 'pointer', transition: 'border-color 0.2s' }}
              onClick={() => setActiveSection('subscription')}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = '#00ffff'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = '#333'}
            >
              <CardContent style={{ padding: '1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Crown className="w-5 h-5" style={{ color: '#00ffff' }} />
                  <div>
                    <p style={{ fontWeight: '600', fontSize: '0.9rem' }}>Gerenciar Assinatura</p>
                    <p style={{ color: '#aaa', fontSize: '0.75rem' }}>Alterar plano ou cancelar</p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4" style={{ color: '#888' }} />
              </CardContent>
            </Card>

            <Card
              style={{ background: '#1a1a1a', border: '1px solid #333', cursor: 'pointer', transition: 'border-color 0.2s' }}
              onClick={openCustomerPortal}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = '#00ffff'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = '#333'}
            >
              <CardContent style={{ padding: '1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <CreditCard className="w-5 h-5" style={{ color: '#22c55e' }} />
                  <div>
                    <p style={{ fontWeight: '600', fontSize: '0.9rem' }}>Formas de Pagamento</p>
                    <p style={{ color: '#aaa', fontSize: '0.75rem' }}>Cartões e dados de faturamento</p>
                  </div>
                </div>
                {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" style={{ color: '#888' }} />}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Subscription */}
      {activeSection === 'subscription' && summary && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {/* Current Plan */}
          <Card style={{ background: '#1a1a1a', border: `1px solid ${planColors[summary.plan] || '#333'}` }}>
            <CardHeader>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <CardDescription>A ASSINATURA ATUAL</CardDescription>
                  <CardTitle style={{ fontSize: '1.3rem', marginTop: '0.5rem' }}>{summary.plan_name}</CardTitle>
                </div>
                <Badge style={{ fontSize: '1.1rem', padding: '0.5rem 1rem', background: `${planColors[summary.plan]}20`, color: planColors[summary.plan] }}>
                  {summary.plan === 'free' ? 'Gratis' : `${formatCurrency(summary.plan_price)}/mes`}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Coins className="w-4 h-4" style={{ color: '#f59e0b' }} />
                  <span style={{ fontSize: '0.9rem' }}>
                    Créditos disponíveis: <strong style={{ color: '#f59e0b' }}>
                      {summary.credits_available >= 999999 ? 'Ilimitados' : summary.credits_available}
                    </strong>
                  </span>
                </div>
                {summary.consultancy_sessions > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Shield className="w-4 h-4" style={{ color: '#8b5cf6' }} />
                    <span style={{ fontSize: '0.9rem' }}>
                      Sessões de consultoria: <strong>{summary.consultancy_sessions - summary.consultancy_sessions_used} restantes</strong>
                    </span>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Calendar className="w-4 h-4" style={{ color: '#888' }} />
                  <span style={{ fontSize: '0.9rem', color: '#888' }}>
                    Conta criada em {new Date(summary.created_at).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              </div>

              <Separator style={{ margin: '1.25rem 0' }} />

              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                {summary.plan !== 'empresarial' && (
                  <Button
                    onClick={onNavigatePricing}
                    data-testid="upgrade-plan-btn"
                    style={{ background: '#00ffff', color: '#000', fontWeight: '600' }}
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Atualizar Assinatura
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={openCustomerPortal}
                  disabled={portalLoading}
                  data-testid="manage-subscription-btn"
                >
                  {portalLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                  Gerenciar no Stripe
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Credits Info */}
          <Card style={{ background: '#1a1a1a', border: '1px solid #333' }}>
            <CardHeader>
              <CardTitle style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Coins className="w-5 h-5" style={{ color: '#f59e0b' }} />
                Creditos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                <div style={{ background: '#0f0f0f', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
                <p style={{ color: '#aaa', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Disponíveis</p>
                  <p style={{ fontSize: '1.25rem', fontWeight: '700', color: '#f59e0b' }}>
                    {summary.credits_available >= 999999 ? '∞' : summary.credits_available}
                  </p>
                </div>
                <div style={{ background: '#0f0f0f', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
                  <p style={{ color: '#aaa', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Usados (mês)</p>
                  <p style={{ fontSize: '1.25rem', fontWeight: '700', color: '#00ffff' }}>{summary.credits_used_this_month}</p>
                </div>
                <div style={{ background: '#0f0f0f', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
                  <p style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Comprados</p>
                  <p style={{ fontSize: '1.25rem', fontWeight: '700', color: '#22c55e' }}>{summary.purchased_credits}</p>
                </div>
              </div>
              <Button variant="outline" onClick={onNavigatePricing} size="sm" data-testid="buy-credits-btn">
                <Wallet className="w-4 h-4 mr-2" />
                Comprar Créditos Avulsos
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Payment Methods */}
      {activeSection === 'payments' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <Card style={{ background: '#1a1a1a', border: '1px solid #333' }}>
            <CardHeader>
              <CardTitle style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <CreditCard className="w-5 h-5" style={{ color: '#00ffff' }} />
                Formas de Pagamento
              </CardTitle>
              <CardDescription>
                Gerencie seus cartões, dados de faturamento e métodos de pagamento através do portal seguro do Stripe
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div style={{ background: 'rgba(0, 255, 255, 0.05)', border: '1px solid rgba(0, 255, 255, 0.2)', borderRadius: '8px', padding: '1.25rem', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <Shield className="w-5 h-5" style={{ color: '#00ffff', flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <p style={{ fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Portal Seguro do Stripe</p>
                    <p style={{ color: '#aaa', fontSize: '0.85rem', lineHeight: '1.5' }}>
                      Seus dados de pagamento são gerenciados diretamente pelo Stripe, garantindo a máxima segurança.
                      Ao clicar no botão abaixo, você será redirecionado para o portal oficial onde poderá:
                    </p>
                    <ul style={{ color: '#aaa', fontSize: '0.85rem', paddingLeft: '1.25rem', marginTop: '0.5rem' }}>
                      <li>Adicionar ou remover cartões de crédito</li>
                      <li>Atualizar dados de faturamento (nome, endereço, telefone)</li>
                      <li>Definir método de pagamento padrão</li>
                      <li>Visualizar e baixar faturas</li>
                      <li>Atualizar ou cancelar assinatura</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Button
                onClick={openCustomerPortal}
                disabled={portalLoading}
                data-testid="open-stripe-portal-btn"
                style={{ background: '#00ffff', color: '#000', fontWeight: '600' }}
              >
                {portalLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                Abrir Portal de Pagamentos
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Payment History */}
      {activeSection === 'history' && (
        <Card style={{ background: '#1a1a1a', border: '1px solid #333' }}>
          <CardHeader>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <CardTitle style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FileText className="w-5 h-5" style={{ color: '#00ffff' }} />
                Histórico de Faturas
              </CardTitle>
              <Badge variant="secondary">{history.length} transações</Badge>
            </div>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                <Wallet style={{ width: '48px', height: '48px', margin: '0 auto 1rem', opacity: 0.4 }} />
                <p>Nenhuma transação registrada</p>
                <p style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>Suas faturas aparecerão aqui após a primeira compra</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #333' }}>
                      {['DATA', 'TIPO', 'VALOR', 'STATUS'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '0.75rem', color: '#888', fontSize: '0.7rem', fontWeight: '600', letterSpacing: '0.05em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((tx, i) => {
                      const st = getStatusStyle(tx.status);
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid #222' }}>
                          <td style={{ padding: '0.75rem', fontSize: '0.85rem' }}>
                            {tx.date ? new Date(tx.date).toLocaleDateString('pt-BR') : '-'}
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            <span style={{ fontSize: '0.8rem', color: '#ccc' }}>{typeLabels[tx.type] || tx.type}</span>
                            {tx.plan && <span style={{ display: 'block', fontSize: '0.7rem', color: '#888' }}>{tx.plan}</span>}
                          </td>
                          <td style={{ padding: '0.75rem', fontWeight: '600', fontSize: '0.85rem', color: '#22c55e' }}>
                            {formatCurrency(tx.amount)}
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                              background: st.bg, color: st.color,
                              padding: '0.25rem 0.75rem', borderRadius: '999px',
                              fontSize: '0.75rem', fontWeight: '600'
                            }}>
                              {st.icon} {st.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <Separator style={{ margin: '1rem 0' }} />
            <Button variant="outline" size="sm" onClick={openCustomerPortal} disabled={portalLoading}>
              <ExternalLink className="w-3.5 h-3.5 mr-2" />
              Ver faturas detalhadas no Stripe
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MyAccountPage;
